import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { exportMeetingMinutes } from '@/utils/exportUtils';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_SECTIONS = [
  { id: '1', title: 'Attendees', content: '', type: 'text' },
  { id: '2', title: 'Agenda', content: '', type: 'text' },
  { id: '3', title: 'Discussion Items', content: '', type: 'text' },
  { id: '4', title: 'Decisions Made', content: '', type: 'text' },
  { id: '5', title: 'Action Items', content: '', type: 'action_items' },
  { id: '6', title: 'Next Meeting', content: '', type: 'text' }
];

export const MeetingMinutesTab = ({ projectId }) => {
  const [minutes, setMinutes] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedMinute, setSelectedMinute] = useState(null);
  const [sections, setSections] = useState(DEFAULT_SECTIONS);
  const [isCloning, setIsCloning] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [viewMode, setViewMode] = useState('card'); // 'card' or 'list'
  const [sendingEmail, setSendingEmail] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    meeting_date: '',
    attendees: [],
    agenda: '',
    notes: '',
    action_items: ''
  });
  const { user } = useAuth();

  useEffect(() => {
    fetchMeetingMinutes();
  }, []);

  const fetchMeetingMinutes = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}/meeting-minutes`);
      setMinutes(response.data);
    } catch (error) {
      toast.error('Failed to load meeting minutes');
    }
  };

  const addSection = () => {
    const newSection = {
      id: Date.now().toString(),
      title: 'New Section',
      content: '',
      type: 'text'
    };
    setSections([...sections, newSection]);
  };

  const updateSection = (id, field, value) => {
    setSections(sections.map(s => s.id === id ? { ...s, [field]: value } : s));
  };

  const deleteSection = (id) => {
    setSections(sections.filter(s => s.id !== id));
  };

  const createMeetingMinute = async (e) => {
    e.preventDefault();
    
    // Build content from sections
    const attendeesSection = sections.find(s => s.title === 'Attendees');
    const agendaSection = sections.find(s => s.title === 'Agenda');
    const actionItemsSection = sections.find(s => s.type === 'action_items');
    
    const notesContent = sections
      .filter(s => s.title !== 'Attendees' && s.title !== 'Agenda' && s.type !== 'action_items')
      .map(s => `## ${s.title}\n${s.content}`)
      .join('\n\n');

    try {
      const data = {
        title: formData.title,
        meeting_date: formData.meeting_date,
        attendees: attendeesSection?.content.split(',').map(a => a.trim()).filter(a => a) || [],
        agenda: agendaSection?.content || '',
        notes: notesContent,
        action_items: actionItemsSection?.content || ''
      };

      await axios.post(`${API}/projects/${projectId}/meeting-minutes`, data);
      toast.success(isCloning ? 'Meeting minutes cloned!' : 'Meeting minutes created!');
      setShowCreateModal(false);
      setSections(DEFAULT_SECTIONS);
      setFormData({ title: '', meeting_date: '', attendees: [], agenda: '', notes: '', action_items: '' });
      setIsCloning(false);
      fetchMeetingMinutes();
    } catch (error) {
      toast.error('Failed to create meeting minutes');
    }
  };

  const cloneMeetingMinute = (minute) => {
    // Parse the notes back into sections
    const clonedSections = [...DEFAULT_SECTIONS];
    
    // Set attendees
    const attendeesSection = clonedSections.find(s => s.title === 'Attendees');
    if (attendeesSection) {
      attendeesSection.content = minute.attendees.join(', ');
    }
    
    // Set agenda
    const agendaSection = clonedSections.find(s => s.title === 'Agenda');
    if (agendaSection) {
      agendaSection.content = minute.agenda || '';
    }
    
    // Set action items
    const actionItemsSection = clonedSections.find(s => s.type === 'action_items');
    if (actionItemsSection) {
      actionItemsSection.content = minute.action_items || '';
    }
    
    // Parse notes sections (they're formatted as ## Title\nContent)
    if (minute.notes) {
      const noteSections = minute.notes.split('## ').filter(s => s.trim());
      noteSections.forEach(noteSection => {
        const [title, ...contentParts] = noteSection.split('\n');
        const content = contentParts.join('\n').trim();
        const existingSection = clonedSections.find(s => s.title === title.trim());
        if (existingSection) {
          existingSection.content = content;
        }
      });
    }
    
    setSections(clonedSections);
    setFormData({
      title: `${minute.title} (Copy)`,
      meeting_date: new Date().toISOString().split('T')[0], // Today's date
      attendees: minute.attendees,
      agenda: minute.agenda,
      notes: minute.notes,
      action_items: minute.action_items
    });
    setIsCloning(true);
    setShowDetailModal(false);
    setShowCreateModal(true);
  };

  const deleteMeetingMinute = async (minuteId, e) => {
    if (e) e.stopPropagation(); // Prevent card click
    if (!window.confirm('Are you sure you want to delete this meeting minute?')) return;
    
    try {
      await axios.delete(`${API}/meeting-minutes/${minuteId}`);
      toast.success('Meeting minute deleted');
      setShowDetailModal(false);
      fetchMeetingMinutes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete meeting minute');
    }
  };

  const sendMeetingMinutesToAttendees = async (minuteId) => {
    setSendingEmail(true);
    try {
      const response = await axios.post(`${API}/meeting-minutes/${minuteId}/send-email`);
      const { sent_to, total_attendees, failed } = response.data;
      
      if (failed && failed.length > 0) {
        toast.success(`Meeting minutes sent to ${sent_to} of ${total_attendees} attendees. Failed: ${failed.join(', ')}`);
      } else {
        toast.success(`Meeting minutes sent to ${sent_to} attendee${sent_to > 1 ? 's' : ''}!`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send meeting minutes');
    } finally {
      setSendingEmail(false);
    }
  };

  const editMeetingMinute = (minute) => {
    // Parse the notes back into sections
    const editSections = [...DEFAULT_SECTIONS];
    
    // Set attendees
    const attendeesSection = editSections.find(s => s.title === 'Attendees');
    if (attendeesSection) {
      attendeesSection.content = minute.attendees.join(', ');
    }
    
    // Set agenda
    const agendaSection = editSections.find(s => s.title === 'Agenda');
    if (agendaSection) {
      agendaSection.content = minute.agenda || '';
    }
    
    // Set action items
    const actionItemsSection = editSections.find(s => s.type === 'action_items');
    if (actionItemsSection) {
      actionItemsSection.content = minute.action_items || '';
    }
    
    // Parse notes sections (they're formatted as ## Title\nContent)
    if (minute.notes) {
      const noteSections = minute.notes.split('## ').filter(s => s.trim());
      noteSections.forEach(noteSection => {
        const [title, ...contentParts] = noteSection.split('\n');
        const content = contentParts.join('\n').trim();
        const existingSection = editSections.find(s => s.title === title.trim());
        if (existingSection) {
          existingSection.content = content;
        }
      });
    }
    
    setSections(editSections);
    setFormData({
      title: minute.title,
      meeting_date: minute.meeting_date,
      attendees: minute.attendees,
      agenda: minute.agenda,
      notes: minute.notes,
      action_items: minute.action_items
    });
    setSelectedMinute(minute);
    setIsEditing(true);
    setShowDetailModal(false);
    setShowEditModal(true);
  };

  const updateMeetingMinute = async (e) => {
    e.preventDefault();
    
    // Build content from sections
    const attendeesSection = sections.find(s => s.title === 'Attendees');
    const agendaSection = sections.find(s => s.title === 'Agenda');
    const actionItemsSection = sections.find(s => s.type === 'action_items');
    
    const notesContent = sections
      .filter(s => s.title !== 'Attendees' && s.title !== 'Agenda' && s.type !== 'action_items')
      .map(s => `## ${s.title}\n${s.content}`)
      .join('\n\n');

    try {
      const data = {
        title: formData.title,
        meeting_date: formData.meeting_date,
        attendees: attendeesSection?.content.split(',').map(a => a.trim()).filter(a => a) || [],
        agenda: agendaSection?.content || '',
        notes: notesContent,
        action_items: actionItemsSection?.content || ''
      };

      await axios.patch(`${API}/meeting-minutes/${selectedMinute.id}`, data);
      toast.success('Meeting minutes updated!');
      setShowEditModal(false);
      setSections(DEFAULT_SECTIONS);
      setFormData({ title: '', meeting_date: '', attendees: [], agenda: '', notes: '', action_items: '' });
      setIsEditing(false);
      setSelectedMinute(null);
      fetchMeetingMinutes();
    } catch (error) {
      toast.error('Failed to update meeting minutes');
    }
  };

  return (
    <div className="space-y-4" data-testid="meeting-minutes-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Meeting Minutes</h2>
          <p className="text-sm text-slate-600 mt-1">Document project meetings with custom sections</p>
        </div>
        <div className="flex gap-2 items-center">
          {/* View Toggle */}
          <div className="flex rounded-md border border-slate-200 overflow-hidden mr-2">
            <button
              onClick={() => setViewMode('card')}
              className={`px-3 py-2 text-sm flex items-center gap-1 ${
                viewMode === 'card' ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
              }`}
              data-testid="view-card-btn"
              title="Card View"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
              Cards
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-2 text-sm flex items-center gap-1 ${
                viewMode === 'list' ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
              }`}
              data-testid="view-list-btn"
              title="List View"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
              </svg>
              List
            </button>
          </div>
          <button
            onClick={() => exportMeetingMinutes(minutes, 'excel')}
            className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 text-sm"
            disabled={minutes.length === 0}
          >
            Export Excel
          </button>
          <button
            onClick={() => exportMeetingMinutes(minutes, 'pdf')}
            className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 text-sm"
            disabled={minutes.length === 0}
          >
            Export PDF
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-slate-900 text-white px-4 py-2 rounded-md hover:bg-slate-800"
            data-testid="create-minutes-button"
          >
            Create Meeting Minutes
          </button>
        </div>
      </div>

      {/* Card View */}
      {viewMode === 'card' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {minutes.map((minute) => (
            <div
              key={minute.id}
              className="bg-white rounded-lg border border-slate-200 p-6 hover:shadow-md transition-shadow cursor-pointer relative group"
              data-testid={`minute-card-${minute.id}`}
            >
              <div onClick={() => { setSelectedMinute(minute); setShowDetailModal(true); }}>
                <h3 className="text-xl font-bold text-slate-800 mb-2">{minute.title}</h3>
                <div className="text-sm text-slate-600 mb-3">
                  {new Date(minute.meeting_date).toLocaleDateString('en-US', { 
                    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' 
                  })}
                </div>
                <div className="text-xs text-slate-500">
                  {minute.attendees.length} attendees
                </div>
              </div>
              {/* Delete button for admin */}
              {user?.role === 'admin' && (
                <button
                  onClick={(e) => deleteMeetingMinute(minute.id, e)}
                  className="absolute top-2 right-2 p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`delete-minute-card-${minute.id}`}
                  title="Delete"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          <table className="w-full" data-testid="meeting-minutes-table">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Title</th>
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Date</th>
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Attendees</th>
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Actions</th>
              </tr>
            </thead>
            <tbody>
              {minutes.map((minute) => (
                <tr 
                  key={minute.id} 
                  className="border-b hover:bg-slate-50 cursor-pointer"
                  data-testid={`minute-row-${minute.id}`}
                >
                  <td 
                    className="p-4 font-medium text-slate-900"
                    onClick={() => { setSelectedMinute(minute); setShowDetailModal(true); }}
                  >
                    {minute.title}
                  </td>
                  <td 
                    className="p-4 text-sm text-slate-600"
                    onClick={() => { setSelectedMinute(minute); setShowDetailModal(true); }}
                  >
                    {new Date(minute.meeting_date).toLocaleDateString('en-US', { 
                      year: 'numeric', month: 'short', day: 'numeric' 
                    })}
                  </td>
                  <td 
                    className="p-4"
                    onClick={() => { setSelectedMinute(minute); setShowDetailModal(true); }}
                  >
                    <div className="flex flex-wrap gap-1">
                      {minute.attendees.slice(0, 3).map((attendee, i) => (
                        <span key={i} className="px-2 py-0.5 bg-slate-100 rounded text-xs">{attendee}</span>
                      ))}
                      {minute.attendees.length > 3 && (
                        <span className="px-2 py-0.5 bg-slate-200 rounded text-xs">+{minute.attendees.length - 3} more</span>
                      )}
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex gap-2">
                      <button
                        onClick={() => { setSelectedMinute(minute); setShowDetailModal(true); }}
                        className="text-sm text-blue-600 hover:underline"
                      >
                        View
                      </button>
                      <button
                        onClick={() => cloneMeetingMinute(minute)}
                        className="text-sm text-indigo-600 hover:underline"
                      >
                        Clone
                      </button>
                      {user?.role === 'admin' && (
                        <button
                          onClick={(e) => deleteMeetingMinute(minute.id, e)}
                          className="text-sm text-red-600 hover:underline"
                          data-testid={`delete-minute-row-${minute.id}`}
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {minutes.length === 0 && (
            <div className="p-8 text-center text-slate-500">
              No meeting minutes yet. Click "Create Meeting Minutes" to add one.
            </div>
          )}
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowCreateModal(false)}>
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">{isCloning ? 'Clone Meeting Minutes' : 'Create Meeting Minutes'}</h2>
            <form onSubmit={createMeetingMinute} className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Meeting Title *</label>
                  <input 
                    type="text" 
                    value={formData.title} 
                    onChange={(e) => setFormData({...formData, title: e.target.value})} 
                    className="w-full px-3 py-2 border border-slate-200 rounded-md" 
                    required 
                    placeholder="Weekly Project Status Meeting"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Meeting Date *</label>
                  <input 
                    type="date" 
                    value={formData.meeting_date} 
                    onChange={(e) => setFormData({...formData, meeting_date: e.target.value})} 
                    className="w-full px-3 py-2 border border-slate-200 rounded-md" 
                    required 
                  />
                </div>
              </div>

              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-slate-900">Sections</h3>
                  <button 
                    type="button" 
                    onClick={addSection}
                    className="text-sm bg-slate-100 hover:bg-slate-200 px-3 py-1 rounded-md"
                  >
                    + Add Section
                  </button>
                </div>

                <div className="space-y-4">
                  {sections.map((section, index) => (
                    <div key={section.id} className="border border-slate-200 rounded-lg p-4 bg-slate-50">
                      <div className="flex items-center justify-between mb-3 gap-3">
                        <div className="flex-1">
                          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">
                            Section Heading
                          </label>
                          <input
                            type="text"
                            value={section.title}
                            onChange={(e) => updateSection(section.id, 'title', e.target.value)}
                            className="w-full font-semibold text-slate-900 bg-white border border-slate-200 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-slate-900"
                            placeholder="Enter custom heading..."
                          />
                        </div>
                        {index >= 6 && (
                          <button
                            type="button"
                            onClick={() => deleteSection(section.id)}
                            className="text-red-600 hover:text-red-800 text-sm mt-5"
                          >
                            Delete
                          </button>
                        )}
                      </div>
                      <textarea
                        value={section.content}
                        onChange={(e) => updateSection(section.id, 'content', e.target.value)}
                        className="w-full px-3 py-2 border border-slate-200 rounded-md bg-white"
                        rows={section.type === 'action_items' ? 4 : 3}
                        placeholder={
                          section.title === 'Attendees' ? 'John Doe, Jane Smith, ...' :
                          section.type === 'action_items' ? 'Action Item 1\nAction Item 2\n...' :
                          `Enter ${section.title.toLowerCase()} details...`
                        }
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button type="submit" className="flex-1 bg-slate-900 text-white py-2 rounded-md hover:bg-slate-800">
                  Create Meeting Minutes
                </button>
                <button 
                  type="button" 
                  onClick={() => { setShowCreateModal(false); setSections(DEFAULT_SECTIONS); setIsCloning(false); }} 
                  className="flex-1 border border-slate-200 py-2 rounded-md hover:bg-slate-50"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedMinute && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowDetailModal(false)}>
          <div className="bg-white rounded-lg p-8 w-full max-w-4xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="mb-6">
              <h2 className="text-3xl font-bold text-slate-900 mb-2">{selectedMinute.title}</h2>
              <div className="text-slate-600">
                {new Date(selectedMinute.meeting_date).toLocaleDateString('en-US', { 
                  weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' 
                })}
              </div>
            </div>

            <div className="space-y-6 prose max-w-none">
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">Attendees</h3>
                <div className="flex flex-wrap gap-2">
                  {selectedMinute.attendees.map((attendee, i) => (
                    <span key={i} className="px-3 py-1 bg-slate-100 rounded-full text-sm">{attendee}</span>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">Agenda</h3>
                <p className="text-slate-700 whitespace-pre-wrap">{selectedMinute.agenda}</p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">Notes</h3>
                <div className="text-slate-700 whitespace-pre-wrap">{selectedMinute.notes}</div>
              </div>

              {selectedMinute.action_items && (
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">Action Items</h3>
                  <ul className="list-disc list-inside space-y-1">
                    {selectedMinute.action_items.split('\n').filter(item => item.trim()).map((item, i) => (
                      <li key={i} className="text-slate-700">{item}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="mt-6 flex gap-3 flex-wrap">
              <button 
                onClick={() => editMeetingMinute(selectedMinute)}
                className="flex-1 bg-amber-600 text-white py-2 rounded-md hover:bg-amber-700 flex items-center justify-center gap-2"
                data-testid="edit-minute-button"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Edit
              </button>
              <button 
                onClick={() => sendMeetingMinutesToAttendees(selectedMinute.id)}
                disabled={sendingEmail}
                className="flex-1 bg-indigo-600 text-white py-2 rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                data-testid="send-to-attendees-button"
              >
                {sendingEmail ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Sending...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    Email
                  </>
                )}
              </button>
              <button 
                onClick={() => cloneMeetingMinute(selectedMinute)}
                className="flex-1 bg-slate-900 text-white py-2 rounded-md hover:bg-slate-800"
                data-testid="clone-minute-button"
              >
                Clone
              </button>
              {user?.role === 'admin' && (
                <button 
                  onClick={() => deleteMeetingMinute(selectedMinute.id)}
                  className="px-6 bg-red-600 text-white py-2 rounded-md hover:bg-red-700"
                  data-testid="delete-minute-detail-button"
                >
                  Delete
                </button>
              )}
              <button 
                onClick={() => setShowDetailModal(false)} 
                className="flex-1 border border-slate-200 py-2 rounded-md hover:bg-slate-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedMinute && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => { setShowEditModal(false); setIsEditing(false); }}>
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="edit-minute-modal">
            <h2 className="text-2xl font-bold text-slate-900 mb-6">Edit Meeting Minutes</h2>
            
            <form onSubmit={updateMeetingMinute} className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Meeting Title</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900"
                    required
                    data-testid="edit-minute-title"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Meeting Date</label>
                  <input
                    type="date"
                    value={formData.meeting_date}
                    onChange={(e) => setFormData({...formData, meeting_date: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900"
                    required
                    data-testid="edit-minute-date"
                  />
                </div>
              </div>

              {/* Sections */}
              <div className="space-y-4">
                {sections.map((section, index) => (
                  <div key={index} className="border border-slate-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-semibold text-slate-900">{section.title}</h3>
                      {section.removable && (
                        <button
                          type="button"
                          onClick={() => setSections(sections.filter((_, i) => i !== index))}
                          className="text-red-500 hover:text-red-700"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                    <textarea
                      value={section.content}
                      onChange={(e) => {
                        const newSections = [...sections];
                        newSections[index].content = e.target.value;
                        setSections(newSections);
                      }}
                      placeholder={section.placeholder}
                      rows={section.type === 'action_items' ? 4 : 3}
                      className="w-full px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900"
                    />
                  </div>
                ))}
              </div>

              <div className="flex gap-3">
                <button
                  type="submit"
                  className="flex-1 bg-slate-900 text-white py-2 rounded-md hover:bg-slate-800"
                  data-testid="update-minute-button"
                >
                  Update Meeting Minutes
                </button>
                <button
                  type="button"
                  onClick={() => { setShowEditModal(false); setIsEditing(false); setSections(DEFAULT_SECTIONS); }}
                  className="flex-1 border border-slate-200 py-2 rounded-md hover:bg-slate-50"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};